// Provider-agnostic query layer. Nothing here imports the Giraffe SDK — the SDK
// side (src/giraffe/) implements `GiraffeData`, and the formula engine and live
// blocks consume it. That keeps the engine testable with plain fixtures.

import type { MeasureClass, SourceKind } from '@/model/types';
import type { UnitSystem } from './units';

export type { SourceKind, MeasureClass };
export type { UnitSystem };

/*
 * How a number off a baked section may be aggregated. (`MeasureClass` itself is
 * declared in model/types.ts, because an override of it is part of the saved
 * document.)
 *
 * The engine emits one section per stack carrying `levels: n` where
 * `grossArea`/`netArea`/`saleableArea` ALREADY have `n` applied and `area` does
 * not, and where `salePrice`/`hardCost`/`efficiency`/`floorToFloor` are
 * per-unit rates. Summing a rate down a building is meaningless, and it is
 * meaningless *quietly* — the number looks entirely plausible. See
 * sdk-pitfalls.md §9.
 *
 * Default an unknown field to `rate`, the class that refuses to aggregate.
 *
 * `nested` is the fifth: a value that is itself an object or a list — a usage's
 * dwelling mix, say. It can be shown, but it cannot be aggregated and it must
 * never be written back from a cell, because a cell holds a summary of it, not
 * the thing itself.
 */

export interface DataRow {
  /**
   * Collection key: feature id PLUS a geometry signature. Baked-section ids are
   * NOT unique — every instance of a block carries the block *definition's* id,
   * so a flow that stamps one block down a line delivers N rows under one id.
   * Keying a Map by the bare id keeps one and silently drops the rest.
   */
  key: string;
  /** The source feature's own id. Valid for write-back, invalid as a map key. */
  id: string;
  properties: Record<string, unknown>;
}

export interface GiraffeData {
  /** Bumped whenever any source changes. Drives volatile recalculation. */
  readonly version: number;
  /**
   * A real project on the other end, as opposed to the demo fixture.
   *
   * Anything that WRITES has to know: offline every write is refused, and an
   * error appearing because someone typed in the demo is noise.
   */
  readonly live: boolean;
  rows(source: SourceKind): readonly DataRow[];
  /** Every distinct property key across the source, sorted. */
  fields(source: SourceKind): readonly string[];
  classOf(source: SourceKind, field: string): MeasureClass;
  /** Row keys selected on the map, or null when the user has nothing selected. */
  selectionKeys(): ReadonlySet<string> | null;
  /**
   * Block DEFINITIONS by id — the types, not their placements.
   *
   * A source rather than a direct SDK read so the Blocks sheet works against
   * the fixture too, which is the only way it is testable without a project.
   */
  blocks(): Readonly<Record<string, { id: string; name?: string; properties?: Record<string, unknown> }>>;
  /**
   * Area of the PROJECT BOUNDARY, or null when there is no project.
   *
   * Deliberately not the sum of section footprints: those are two different
   * numbers, and a plot ratio built on the wrong one is overstated by however
   * much of the site is not built on.
   *
   * In the units `units()` reports, like every other number here.
   */
  siteArea(): number | null;
  /**
   * What the app INFERRED a field's class to be, before any user override.
   * `classOf` is the answer to use; this is for saying "inferred, and here is
   * how to change it".
   */
  inferredClassOf?(source: SourceKind, field: string): MeasureClass;
  /**
   * The geometry of a feature, by id — what "zoom to this row" needs and the
   * only thing in this interface that is about WHERE rather than what.
   * Optional: a fixture has no map to fly.
   */
  geometryFor?(id: string): unknown;
  /**
   * The drawing layers a feature may be put ON, by `layerId`.
   *
   * Here rather than derived from the rows because a project can hold a layer
   * nothing has been drawn on yet, and that is exactly the layer someone wants
   * to move a feature to. Optional: a fixture has no layer tree.
   *
   * In the project's own layer-tree order (best-effort — see
   * `giraffe/normalize.ts`'s `drawingLayerNames`), NOT alphabetised —
   * `entityTable.ts`'s `sortRows` uses this order to sort Features/Areas
   * rows by layer. A fixture built for a test controls this order directly
   * (whatever array it was constructed with); a caller that wants an
   * alphabetical list instead (a dropdown) sorts its own copy.
   */
  drawingLayers?(): readonly string[];
  /**
   * Which of `drawingLayers()`'s own names are not switched off in the
   * project's layer panel right now — for a sheet filter's "Visible only"
   * option, so it means the same thing as the eye icon someone is actually
   * looking at. Optional, same as `drawingLayers` — and even when present,
   * the project's own visibility signal is undocumented (see
   * `giraffe/normalize.ts`'s `visibleDrawingLayerNames`), so this is
   * best-effort: a layer this cannot read a visibility bit for is assumed
   * visible, never hidden by a guess.
   */
  visibleDrawingLayers?(): readonly string[];
  /**
   * Which measurement system the numbers above are in.
   *
   * The provider SAYS what it is handing out, rather than every reader having
   * to ask a store what units are in force — so a header can label itself and a
   * write-back knows what it is undoing. Absent means metric, which is what
   * Giraffe's own numbers always are: only `withDisplayUnits` answers anything
   * else. See data/units.ts.
   */
  units?(): UnitSystem;
}

/** An empty provider, so the engine runs with no Giraffe attached. */
export const EMPTY_DATA: GiraffeData = {
  version: 0,
  live: false,
  blocks: () => ({}),
  rows: () => [],
  fields: () => [],
  classOf: () => 'rate',
  selectionKeys: () => null,
  siteArea: () => null,
};
